import React, { useEffect, useState, useRef, useMemo } from "react";
import io from "socket.io-client";
import "./Dashboard.css";
import { API_BASE_URL } from "./config";
import send_icon from "./components/images/send.svg";
import upload_icon from "./components/images/upload.svg";
import close_icon from "./components/images/close.svg";
import LoadingDots from "./components/LoadingDots";
import ReactMarkdown from "react-markdown";
import edit_svg from "./components/images/edit.svg";
import Pica from "pica";

function Dashboard() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isAssistantResponding, setIsAssistantResponding] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState(null); // Track current thread ID
  const [userEmail, setUserEmail] = useState(null); // State to store email
  const [threads, setThreads] = useState([]); // State to store threads
  const socketRef = useRef(null);
  const socketInitializedRef = useRef(false); // Ref to track if socket is initialized
  const firstMessageReceived = useRef(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupContent, setPopupContent] = useState("");
  const inputRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const pica = new Pica(); // Pica instance for resizing

  const compressImage = (file, maxWidth, maxHeight, quality) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;

        img.onload = () => {
          // Calculate the new dimensions
          let ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;

          // Use pica to resize the image
          pica
            .resize(img, canvas)
            .then(() => {
              return pica.toBlob(canvas, "image/jpeg", quality);
            })
            .then((blob) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64data = reader.result.split(",")[1];
                resolve(base64data);
              };
              reader.readAsDataURL(blob);
            })
            .catch((err) => {
              reject(err);
            });
        };

        img.onerror = (err) => {
          reject(err);
        };
      };

      reader.onerror = (err) => {
        reject(err);
      };

      reader.readAsDataURL(file);
    });
  };

  const handleNewConversation = () => {
    setMessages([]); // Clear the existing messages
    setCurrentThreadId(null); // Reset the thread ID to null
  };

  const handleImageClick = (metadata) => {
    if (metadata) {
      setPopupContent(metadata);
      setIsPopupVisible(true);
    }
  };

  const closePopup = () => {
    setIsPopupVisible(false);
    setPopupContent("");
  };

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    threadId: null,
  });

  const handleRightClick = (event, threadId) => {
    event.preventDefault(); // Prevent the default context menu from appearing
    setContextMenu({
      visible: true,
      x: event.clientX, // Use clientX for more precise positioning
      y: event.clientY, // Use clientY for more precise positioning
      threadId: threadId,
    });
  };

  const handleDeleteThread = async () => {
    if (contextMenu.threadId) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/delete-thread/${contextMenu.threadId}`,
          {
            method: "DELETE",
          }
        );
        if (response.ok) {
          // Remove the thread from the list
          setThreads(
            threads.filter(
              (thread) => thread.thread_id !== contextMenu.threadId
            )
          );

          // Check if the deleted thread is the current thread
          if (contextMenu.threadId === currentThreadId) {
            // Reset the current thread ID and clear messages
            setCurrentThreadId(null); // Reset current thread ID
            setMessages([]); // Clear messages to show a blank screen
            setIsAssistantResponding(false); // Ensure assistant is not responding
          }

          // Hide the context menu
          setContextMenu({ visible: false, x: 0, y: 0, threadId: null });
        } else {
          console.error("Failed to delete thread.");
        }
      } catch (error) {
        console.error("An error occurred while deleting the thread:", error);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, threadId: null });
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [contextMenu]);

  // Function to categorize and sort threads based on days ago
  const categorizeAndSortThreads = (threads) => {
    const currentTime = Date.now() / 1000; // Current time in seconds

    const categories = {
      Today: [],
      Yesterday: [],
      "2 Days Ago": [],
      "3 Days Ago": [],
      "4 Days Ago": [],
      "5 Days Ago": [],
      "6 Days Ago": [],
      "Last Week": [],
      "2 Weeks Ago": [],
      "3 Weeks Ago": [],
      "Last Month": [],
      "2 Months Ago": [],
      "3 Months Ago": [],
      Archive: [],
    };

    threads.forEach((thread) => {
      const daysAgo = Math.floor((currentTime - thread.timestamp) / 86400);

      if (daysAgo === 0) {
        categories["Today"].push(thread);
      } else if (daysAgo === 1) {
        categories["Yesterday"].push(thread);
      } else if (daysAgo === 2) {
        categories["2 Days Ago"].push(thread);
      } else if (daysAgo === 3) {
        categories["3 Days Ago"].push(thread);
      } else if (daysAgo === 4) {
        categories["4 Days Ago"].push(thread);
      } else if (daysAgo === 5) {
        categories["5 Days Ago"].push(thread);
      } else if (daysAgo === 6) {
        categories["6 Days Ago"].push(thread);
      } else if (daysAgo >= 7 && daysAgo <= 13) {
        categories["Last Week"].push(thread);
      } else if (daysAgo >= 14 && daysAgo <= 20) {
        categories["2 Weeks Ago"].push(thread);
      } else if (daysAgo >= 21 && daysAgo <= 27) {
        categories["3 Weeks Ago"].push(thread);
      } else if (daysAgo >= 28 && daysAgo <= 59) {
        categories["Last Month"].push(thread);
      } else if (daysAgo >= 60 && daysAgo <= 89) {
        categories["2 Months Ago"].push(thread);
      } else if (daysAgo >= 90 && daysAgo <= 119) {
        categories["3 Months Ago"].push(thread);
      } else {
        categories["Archive"].push(thread);
      }
    });

    // Sort each category by timestamp in descending order (newest first)
    const sortThreads = (a, b) => b.timestamp - a.timestamp;

    for (let key in categories) {
      categories[key].sort(sortThreads);
    }

    return categories;
  };

  const categoryOrder = [
    "Today",
    "Yesterday",
    "2 Days Ago",
    "3 Days Ago",
    "4 Days Ago",
    "5 Days Ago",
    "6 Days Ago",
    "Last Week",
    "2 Weeks Ago",
    "3 Weeks Ago",
    "Last Month",
    "2 Months Ago",
    "3 Months Ago",
    "Archive",
  ];

  // Function to validate the user's cookie and fetch the email
  useEffect(() => {
    const validateCookie = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/validate_cookie`, {
          method: "POST",
          credentials: "include", // Include cookies in the request
        });

        if (response.ok) {
          const data = await response.json();
          setUserEmail(data.email); // Save the email in the state

          // Fetch threads by email after validating
          const threadsResponse = await fetch(
            `${API_BASE_URL}/threads/${data.email}`
          );

          if (threadsResponse.ok) {
            const threadsData = await threadsResponse.json();
            setThreads(threadsData); // Save threads to state
          } else {
            console.error("Failed to fetch threads.");
          }
        } else {
          console.error("Failed to authenticate user. Please log in.");
        }
      } catch (error) {
        console.error("An error occurred while validating the cookie:", error);
      }
    };

    validateCookie(); // Call the function to validate cookie on component mount

    const textarea = inputRef.current;

    const handleInput = () => {
      textarea.style.height = "auto";
      const newHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.max(
        38,
        Math.min(newHeight - 30, 100)
      )}px`;
    };

    textarea.addEventListener("input", handleInput);

    return () => {
      textarea.removeEventListener("input", handleInput);
    };
  }, []);

  useEffect(() => {
    const handlePaste = (event) => {
      const clipboardItems = event.clipboardData.items;
      let hasImage = false;
      let hasPlainText = false;
      let hasHtml = false;

      // Loop through clipboard items to check for plain text, HTML, or images
      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];

        if (item.type.startsWith("image/")) {
          hasImage = true;
        } else if (item.type === "text/plain") {
          hasPlainText = true;
        } else if (item.type === "text/html") {
          hasHtml = true;
        }
      }

      // Handle images from any source (Excel, Google Sheets, etc.)
      if (hasImage) {
        event.preventDefault(); // Prevent the default paste behavior
        for (let i = 0; i < clipboardItems.length; i++) {
          const item = clipboardItems[i];

          if (item.type.startsWith("image/")) {
            const imageBlob = item.getAsFile();
            if (imageBlob) {
              const file = new File([imageBlob], "pasted_image.png", {
                type: imageBlob.type,
              });
              setUploadedFiles([file]); // Attach the image
            }
          }
        }
      } else if (hasPlainText) {
        // Let default behavior occur for plain text
        return;
      } else if (hasHtml) {
        // Handle HTML case (like from Google Sheets or Excel cells)
        event.preventDefault(); // Prevent pasting raw HTML

        const htmlItem = clipboardItems[0];
        htmlItem.getAsString((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          const img = doc.querySelector("img");

          if (img) {
            // If an image is embedded in the HTML (Google Sheets or Excel)
            const imgSrc = img.src;

            if (imgSrc.startsWith("data:image")) {
              // If the image source is base64-encoded (common in Google Sheets)
              fetch(imgSrc)
                .then((res) => res.blob())
                .then((blob) => {
                  const file = new File([blob], "pasted_image.png", {
                    type: blob.type,
                  });
                  setUploadedFiles([file]); // Attach the image
                })
                .catch((err) => {
                  console.error("Failed to fetch image from base64:", err);
                });
            } else {
              // Handle cases where imgSrc is a URL
              fetch(imgSrc)
                .then((res) => res.blob())
                .then((blob) => {
                  const file = new File([blob], "pasted_image.png", {
                    type: blob.type,
                  });
                  setUploadedFiles([file]); // Attach the image
                })
                .catch((err) => {
                  console.error("Failed to fetch image from URL:", err);
                });
            }
          } else {
            // If no image is found, assume it's text and extract it
            const text = doc.body.innerText.trim();
            if (text) {
              // Insert the text content into the input field
              inputRef.current.value += text;
            }
          }
        });
      }
    };

    // Add event listener for paste
    const input = inputRef.current;
    input.addEventListener("paste", handlePaste);

    // Cleanup event listener on component unmount
    return () => {
      input.removeEventListener("paste", handlePaste);
    };
  }, []);

  useEffect(() => {
    if (userEmail && !socketInitializedRef.current) {
      // Only initialize if userEmail is set and socket is not yet initialized
      socketRef.current = io(`${API_BASE_URL}/dashboard`, {
        transports: ["websocket", "polling"],
        query: { user_id: userEmail },
        timeout: 300000, // Set the timeout to 300,000 milliseconds (5 minutes)
      });

      socketInitializedRef.current = true; // Mark socket as initialized

      // Heartbeat setup: send a heartbeat message every 30 seconds
      const heartbeatInterval = setInterval(() => {
        if (socketRef.current) {
          socketRef.current.emit("heartbeat", { user_id: userEmail });
        }
      }, 30000); // Send heartbeat every 30 seconds

      socketRef.current.on("new_message", (message) => {
        if (message && message.message) {
          setMessages((prevMessages) => {
            const lastMessageIndex = prevMessages.length - 1;
            const lastMessage = prevMessages[lastMessageIndex];

            if (
              lastMessage &&
              lastMessage.sender === "assistant" &&
              lastMessage.isStreaming
            ) {
              const updatedMessage = {
                ...lastMessage,
                content:
                  typeof lastMessage.content === "string"
                    ? lastMessage.content + message.message
                    : message.message,
              };
              return [
                ...prevMessages.slice(0, lastMessageIndex),
                updatedMessage,
              ];
            } else {
              return [
                ...prevMessages.filter(
                  (msg) =>
                    msg.sender !== "assistant" ||
                    msg.content !== <LoadingDots />
                ),
                {
                  content: message.message,
                  sender: "assistant",
                  isStreaming: true,
                },
              ];
            }
          });
        }
        // Removed updates to currentThreadId and threads here
      });

      socketRef.current.on("message_done", () => {
        setMessages((prevMessages) => {
          const lastMessageIndex = prevMessages.length - 1;
          const lastMessage = prevMessages[lastMessageIndex];

          if (lastMessage && lastMessage.sender === "assistant") {
            const updatedMessage = { ...lastMessage, isStreaming: false };
            return [...prevMessages.slice(0, lastMessageIndex), updatedMessage];
          }
          return prevMessages;
        });
        setIsAssistantResponding(false);
      });

      socketRef.current.on("connect", () => {});

      socketRef.current.on("error", (error) => {
        console.error("WebSocket error:", error);
      });

      // Update in image_update listener
      socketRef.current.on("image_update", (data) => {
        // Update the image_directory of the thread that matches the thread_id in the data
        setThreads((prevThreads) =>
          prevThreads.map((thread) => {
            if (
              thread.thread_id === data.thread_id ||
              (thread.thread_id === "pending" &&
                (!currentThreadId || currentThreadId === "pending"))
            ) {
              return { ...thread, image_directory: data.message };
            }
            return thread;
          })
        );

        // Update the attachments in the last message with attachments
        setMessages((prevMessages) => {
          const lastImageMessageIndex = [...prevMessages]
            .reverse()
            .findIndex((msg) => msg.attachments && msg.attachments.length > 0);

          if (lastImageMessageIndex !== -1) {
            const indexToUpdate =
              prevMessages.length - 1 - lastImageMessageIndex;
            const updatedMessages = [...prevMessages];
            const messageToUpdate = updatedMessages[indexToUpdate];

            updatedMessages[indexToUpdate] = {
              ...messageToUpdate,
              attachments: [data.message], // Replace with actual image URL from server
            };

            return updatedMessages;
          }

          return prevMessages;
        });
      });

      // Update in title_update listener
      socketRef.current.on("title_update", (data) => {
        setThreads((prevThreads) =>
          prevThreads.map((thread) => {
            if (
              thread.thread_id === currentThreadId ||
              thread.thread_id === data.thread_id ||
              (thread.thread_id === "pending" &&
                (!currentThreadId || currentThreadId === "pending"))
            ) {
              return { ...thread, title: data.message };
            }
            return thread;
          })
        );
      });

      socketRef.current.on("new_thread_id", (data) => {
        setCurrentThreadId(data.thread_id);
        setThreads((prevThreads) =>
          prevThreads.map((thread) =>
            thread.thread_id === "pending"
              ? { ...thread, thread_id: data.thread_id }
              : thread
          )
        );
      });

      socketRef.current.on("respell_update", (data) => {
        if (
          currentThreadId === data.thread_id ||
          (currentThreadId === "pending" && data.thread_id === "pending")
        ) {
          setMessages((prevMessages) => {
            const lastImageMessageIndex = [...prevMessages]
              .reverse()
              .findIndex(
                (msg) => msg.attachments && msg.attachments.length > 0
              );

            if (lastImageMessageIndex !== -1) {
              const indexToUpdate =
                prevMessages.length - 1 - lastImageMessageIndex;
              const updatedMessages = [...prevMessages];
              const messageToUpdate = updatedMessages[indexToUpdate];

              updatedMessages[indexToUpdate] = {
                ...messageToUpdate,
                metadata: data.message,
              };

              return updatedMessages;
            }

            return prevMessages;
          });
        }
      });

      // Cleanup function to disconnect socket and clear heartbeat interval
      return () => {
        if (socketRef.current) {
          socketRef.current.disconnect();
          console.log("Socket disconnected");
        }
        clearInterval(heartbeatInterval); // Clear heartbeat interval on cleanup
      };
    }
  }, [userEmail]); // Only run effect when userEmail is set

  useEffect(() => {
    if (isAssistantResponding && chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isAssistantResponding]);

  const sendMessage = () => {
    if (
      (input.trim() !== "" || uploadedFiles.length > 0) &&
      socketRef.current
    ) {
      const userMessage = { content: input, sender: "user", attachments: [] };
      setIsAssistantResponding(true);
      firstMessageReceived.current = false; // Reset for the next round of messages

      // Prepare the payload for the backend
      const payload = {
        prompt: input,
        thread_id: currentThreadId,
      };

      // If there's no current thread ID, include the user email in the payload
      if (!currentThreadId && userEmail) {
        payload.email = userEmail;
      }

      // Check if there is an uploaded file and add its base64 to the payload
      if (uploadedFiles.length > 0) {
        const file = uploadedFiles[0];

        // Compress the image before sending
        compressImage(file, 800, 800, 0.7)
          .then((base64Image) => {
            payload.image = base64Image; // Add compressed base64 encoded image to the payload
            // Temporarily show the image in the chat using URL.createObjectURL
            const tempImageURL = URL.createObjectURL(file);

            // Update user message to include the image attachment
            const userMessageWithImage = {
              ...userMessage,
              attachments: [tempImageURL], // Temporarily show the image
            };

            setMessages((prevMessages) => [
              ...prevMessages,
              userMessageWithImage,
              {
                content: <LoadingDots />,
                sender: "assistant",
                isStreaming: true,
              }, // Display LoadingDots initially
            ]);

            // Send the message with the current thread ID or email to the backend
            socketRef.current.emit("send_message", payload);

            // Reset input and uploaded files
            setInput("");
            setUploadedFiles([]);
            inputRef.current.focus();
          })
          .catch((error) => {
            console.error("Error compressing the image:", error);
          });
      } else {
        // No file uploaded, just update the messages
        setMessages((prevMessages) => [
          ...prevMessages,
          userMessage,
          { content: <LoadingDots />, sender: "assistant", isStreaming: true }, // Display LoadingDots initially
        ]);

        // Send the message with the current thread ID or email to the backend
        socketRef.current.emit("send_message", payload);

        // Reset input and uploaded files
        setInput("");
        setUploadedFiles([]);
        inputRef.current.focus();
      }

      // Check if a new thread is being created
      if (!currentThreadId) {
        const newThread = {
          thread_id: "pending", // Temporary ID, will be updated when backend returns a real ID
          title: "New Thread",
          timestamp: Date.now() / 1000, // Current time as UNIX timestamp
          image_directory: null, // Placeholder for future image (if any)
        };

        // Add the new temporary thread to the state
        setThreads((prevThreads) => [newThread, ...prevThreads]);
        setCurrentThreadId("pending"); // Set current thread ID to the temporary ID
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !isAssistantResponding) {
      event.preventDefault();
      sendMessage();
    }
  };

  // const handleFileUpload = (files) => {
  //   const fileArray = Array.from(files);
  //   setUploadedFiles((prevFiles) => [...prevFiles, ...fileArray]);
  //   console.log("Files uploaded:", fileArray);
  // };

  const handleFileUpload = (files) => {
    if (files.length > 0) {
      const file = files[0]; // Take only the first file
      setUploadedFiles([file]); // Replace the previous file with the new one
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileUpload(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileUpload(event.target.files); // Pass files to the upload handler
      event.target.value = ""; // Reset the input value to allow re-uploading the same file
    }
  };

  const formattedMessages = useMemo(() => {
    return messages.map((message, index) => (
      <div
        key={index}
        className={`message-wrapper ${
          message.sender === "user"
            ? "user-message-wrapper"
            : "assistant-message-wrapper"
        }`}
      >
        {/* Display the image if it exists in attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="message-image">
            <img
              src={message.attachments[0].replace(
                /^https?:\/\/[^/]+/,
                API_BASE_URL
              )}
              alt="Attached"
              className={`chat-image ${message.metadata ? "clickable" : ""}`}
              onClick={
                message.metadata
                  ? () => handleImageClick(message.metadata)
                  : undefined
              }
            />
          </div>
        )}
        {/* Message bubble containing text content */}
        {message.content &&
          (typeof message.content !== "string" ||
            message.content.trim() !== "") && (
            <div
              className={`chat-message ${
                message.sender === "user" ? "user-message" : "assistant-message"
              }`}
            >
              {/* Display the message content */}
              {typeof message.content === "string" ? (
                <ReactMarkdown>{message.content}</ReactMarkdown>
              ) : (
                message.content
              )}
            </div>
          )}
      </div>
    ));
  }, [messages]);

  const categorizedThreads = useMemo(
    () => categorizeAndSortThreads(threads),
    [threads]
  );

  const handleThreadClick = async (threadId) => {
    setCurrentThreadId(threadId); // Update the current thread ID

    // Switch the current thread on the backend
    socketRef.current.emit("switch_thread", { thread_id: threadId });

    try {
      // Fetch messages for the selected thread
      const response = await fetch(
        `${API_BASE_URL}/thread/${threadId}/messages`
      );
      if (response.ok) {
        const data = await response.json();
        // Update messages with all parts of the message object
        setMessages(
          data.reverse().map((msg) => ({
            content: msg.content,
            sender: msg.role,
            createdAt: msg.created_at,
            attachments: msg.attachments || [], // Include attachments
            metadata: msg.metadata || "", // Include metadata
          }))
        );
      } else {
        console.error("Failed to fetch thread messages.");
      }
    } catch (error) {
      console.error("An error occurred while fetching thread messages:", error);
    }
  };
  return (
    <div
      className="dashboard-main-content"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isPopupVisible && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <ReactMarkdown>{popupContent}</ReactMarkdown>
          </div>
        </div>
      )}
      <div className="dashboard-left-side">
        {contextMenu.visible && (
          <div
            className="context-menu"
            style={{
              top: `${contextMenu.y - 73}px`, // Adjust by subtracting navbar height
              left: `${contextMenu.x}px`, // Left position remains the same
              position: "absolute",
            }}
          >
            <div onClick={handleDeleteThread}>Delete Thread</div>
          </div>
        )}
        <div className="new-convo-btn">
          <img
            className="new-conversation-button"
            onClick={handleNewConversation}
            src={edit_svg}
            alt="Edit svg icon"
          />
        </div>
        {categoryOrder.map((categoryName) =>
          categorizedThreads[categoryName] &&
          categorizedThreads[categoryName].length > 0 ? (
            <React.Fragment key={categoryName}>
              <h4>{categoryName}</h4>
              <div className="small-horizontal-line" />
              {categorizedThreads[categoryName].map((thread) => {
                // Replace the domain portion with API_BASE_URL, handling both http and https
                const imageUrl = thread.image_directory
                  ? thread.image_directory.replace(
                      /^https?:\/\/[^/]+/,
                      API_BASE_URL
                    )
                  : null;

                return (
                  <div
                    key={thread.thread_id}
                    className={`thread-item ${
                      thread.thread_id === currentThreadId ? "selected" : ""
                    }`}
                    onClick={() => handleThreadClick(thread.thread_id)}
                    onContextMenu={(e) => handleRightClick(e, thread.thread_id)}
                  >
                    <div className="thread-content">
                      {thread.title || "Untitled Thread"}
                    </div>
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt="Thread Thumbnail"
                        className="thread-thumbnail"
                      />
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ) : null
        )}
      </div>

      <div className="dashboard-right-side">
        <div className="dashboard-chat-container">
          <div className="dashboard-chat">
            {formattedMessages}
            <div ref={chatEndRef} />
          </div>
        </div>
        <div
          className={`dashboard-user-message ${
            uploadedFiles.length > 0 ? "with-files" : ""
          }`}
        >
          <input
            type="file"
            id="file-upload"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
            accept=".jpg, .jpeg, .png"
          />
          <button
            className="upload-message-button"
            onClick={() => document.getElementById("file-upload").click()}
          >
            <img src={upload_icon} alt="Upload" />
          </button>
          <div className="user-message-center">
            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="file-card">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="file-preview"
                    />
                    <span className="file-name">{file.name}</span>
                    <button
                      className="remove-file-button"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <img src={close_icon} alt="Remove" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={inputRef}
              className="user-message-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
            />
          </div>
          <button
            className="send-message-button"
            onClick={sendMessage}
            disabled={isAssistantResponding}
          >
            <img src={send_icon} alt="Send" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
